import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  Pressable,
  Modal,
  ActivityIndicator,
  Platform,
  ScrollView,
} from 'react-native';
import { X, CircleCheck as CheckCircle2 } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { saveEmailCapture, EmailCaptureData } from '@/utils/emailCaptureService';
import { trackLeadSubmit, trackFormEvent, trackFunnelStep } from '@/utils/analytics';

interface EmailCaptureModalProps {
  visible: boolean;
  onClose: () => void;
  type: 'comparison' | 'recommendation';
  mainTopic?: string;
  subTopic?: string;
  coursesShown?: string[];
  conversationId?: string;
  conversationHistory?: any[];
  onSuccess?: () => void;
}

export function EmailCaptureModal({
  visible,
  onClose,
  type,
  mainTopic,
  subTopic,
  coursesShown,
  conversationId,
  conversationHistory,
  onSuccess,
}: EmailCaptureModalProps) {
  const [email, setEmail] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track modal open
  useEffect(() => {
    if (visible) {
      void trackFunnelStep('lead_modal_open', { type });
    }
  }, [visible, type]);

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail('');
      setAgreedToTerms(false);
      setError(null);
      setIsSuccess(false);
      onClose();
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!email.trim()) {
      const msg = 'אנא הזן כתובת מייל';
      setError(msg);
      void trackFormEvent('email_capture', 'validation_error', 'email', msg);
      return;
    }

    if (!agreedToTerms) {
      const msg = 'יש לאשר קבלת עדכונים';
      setError(msg);
      void trackFormEvent('email_capture', 'validation_error', 'terms', msg);
      return;
    }

    void trackFormEvent('email_capture', 'submit_attempt');
    setIsSubmitting(true);

    const captureData: EmailCaptureData = {
      email: email.trim(),
      mainTopic,
      subTopic,
      coursesShown,
      conversationId,
      conversationHistory,
      source: type === 'comparison' ? 'modal-comparison' : 'modal-recommendation',
    };

    const result = await saveEmailCapture(captureData);

    setIsSubmitting(false);

    if (result.success) {
      trackLeadSubmit(email.trim());
      void trackFormEvent('email_capture', 'submit_success');
      void trackFunnelStep('lead_submit', { type, mainTopic });
      setIsSuccess(true);
      if (onSuccess) {
        onSuccess();
      }
      setTimeout(() => {
        handleClose();
      }, 2000);
    } else {
      const errMsg = result.error || 'אירעה שגיאה. אנא נסה שוב.';
      setError(errMsg);
      void trackFormEvent('email_capture', 'submit_fail', undefined, errMsg);
    }
  };

  const getTitle = () => {
    if (type === 'comparison') {
      return 'סיכום ההבדלים בין הקורסים';
    }
    return 'מה הכי מתאים לך?';
  };

  const getSubtitle = () => {
    if (type === 'comparison') {
      return 'נשלח לך מייל עם סיכום מפורט של ההבדלים בין הקורסים השונים';
    }
    return 'אשלח לך מייל עם המלצה אישית על הקורס המתאים לך ביותר';
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        <Pressable
          style={styles.overlayTouchable}
          onPress={handleClose}
          accessible={false}
        />
        <View style={styles.modalContainer}>
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.header}>
              <Pressable
                style={styles.closeButton}
                onPress={handleClose}
                hitSlop={8}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel="סגור"
              >
                <X size={24} color={theme.colors.textLight} strokeWidth={2} />
              </Pressable>
            </View>

            {isSuccess ? (
              <View style={styles.successContainer}>
                <CheckCircle2 size={48} color={theme.colors.success} strokeWidth={2} />
                <Text style={styles.successTitle}>תודה רבה!</Text>
                <Text style={styles.successText}>
                  נשלח לך סיכום מסודר של הקורסים בקרוב 📧
                </Text>
              </View>
            ) : (
              <>
                <Text style={styles.title}>{getTitle()}</Text>
                <Text style={styles.subtitle}>{getSubtitle()}</Text>

                <TextInput
                  style={[
                    styles.input,
                    error && styles.inputError,
                  ]}
                  value={email}
                  onChangeText={(text) => {
                    setEmail(text);
                    setError(null);
                  }}
                  onFocus={() => void trackFormEvent('email_capture', 'focus', 'email')}
                  placeholder="הכנס כתובת מייל"
                  placeholderTextColor={theme.colors.textLight}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  editable={!isSubmitting}
                  accessible={true}
                  accessibilityLabel="שדה הזנת כתובת מייל"
                />

                <Pressable
                  style={styles.checkboxContainer}
                  onPress={() => setAgreedToTerms(!agreedToTerms)}
                  accessible={true}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: agreedToTerms }}
                >
                  <View style={[styles.checkbox, agreedToTerms && styles.checkboxChecked]}>
                    {agreedToTerms && (
                      <CheckCircle2 size={16} color="#FFFFFF" strokeWidth={3} />
                    )}
                  </View>
                  <Text style={styles.checkboxLabel}>
                    אני מאשר/ת קבלת הודעות ועדכונים במייל
                  </Text>
                </Pressable>

                {error && (
                  <Text
                    style={styles.errorText}
                    accessible={true}
                    accessibilityRole="alert"
                  >
                    {error}
                  </Text>
                )}

                <Pressable
                  style={({ pressed }) => [
                    styles.submitButton,
                    pressed && styles.submitButtonPressed,
                    isSubmitting && styles.submitButtonDisabled,
                  ]}
                  onPress={handleSubmit}
                  disabled={isSubmitting}
                  accessible={true}
                  accessibilityRole="button"
                  accessibilityLabel="שלח לי סיכום"
                  accessibilityState={{ disabled: isSubmitting }}
                >
                  {isSubmitting ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <Text style={styles.submitButtonText}>שלח לי סיכום</Text>
                  )}
                </Pressable>
              </>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  overlayTouchable: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    backgroundColor: theme.colors.cardBackground,
    borderRadius: theme.borderRadius.xl,
    ...Platform.select({
      web: {
        boxShadow: `0 0 40px ${theme.colors.primary}50`,
      },
      default: {
        shadowColor: theme.colors.primary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        elevation: 8,
      },
    }),
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: theme.spacing.xl,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing.sm,
  },
  closeButton: {
    padding: theme.spacing.xs,
  },
  title: {
    fontSize: 22,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    textAlign: 'center',
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.sm,
    lineHeight: 32,
  },
  subtitle: {
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'center',
    marginBottom: theme.spacing.xl,
    lineHeight: 22,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    color: '#0B1020',
    textAlign: 'right',
    marginBottom: theme.spacing.md,
    ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
  } as any,
  inputError: {
    borderColor: theme.colors.error,
    borderWidth: 2,
  },
  checkboxContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
    gap: theme.spacing.sm,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 2,
    borderColor: theme.colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.backgroundLight,
  },
  checkboxChecked: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  checkboxLabel: {
    fontSize: 13,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    flex: 1,
    textAlign: 'right',
  },
  errorText: {
    fontSize: 13,
    fontFamily: theme.fonts.regular,
    color: theme.colors.error,
    textAlign: 'right',
    marginBottom: theme.spacing.md,
  },
  submitButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: `0 2px 8px ${theme.colors.primary}40`,
      },
      default: {
        shadowColor: theme.colors.primary,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 4,
        elevation: 4,
      },
    }),
  },
  submitButtonPressed: {
    opacity: 0.8,
    transform: [{ scale: 0.98 }],
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: '#FFFFFF',
  },
  successContainer: {
    alignItems: 'center',
    paddingVertical: theme.spacing.xl,
  },
  successTitle: {
    fontSize: 24,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    textAlign: 'center',
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  successText: {
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'center',
    lineHeight: 24,
  },
});
